/**
 * PhaseGrid Component
 *
 * A data grid component for managing charging infrastructure phases using ag-Grid.
 * Displays and allows editing of phase data including:
 * - Site locations and years
 * - Port configurations and quantities
 * - Cost breakdowns (equipment, installation, utilities, etc.)
 * - Incentives and funding sources
 *
 * Features:
 * - Inline cell editing
 * - Dynamic column visibility based on user access
 * - Automatic data persistence
 * - Responsive layout with horizontal scroll shadow
 * - Pagination
 *
 * @component
 * @param {Object} props
 * @param {Object} props.gridApi - AG Grid API instance (currently unused but maintained for future use)
 * @param {Function} props.setGridApi - Function to store the grid API reference
 * @param {Array} props.rowData - Array of phase data objects to display
 * @param {Function} props.setRowData - Function to update the phase data
 */

import { AgGridReact } from "ag-grid-react"; // React Data Grid Component
import React, { useState, useEffect, useMemo } from "react"; // React State Management
import usePhases from "store/usePhases";
import useAuthStore from "store/useAuthStore";
import useColumnState from "store/useColumnState";
import useProForma from "store/useProForma";
import { updatePhaseField } from "api/phaseApi";
import PropTypes from "prop-types";
import { AllCommunityModule, ModuleRegistry } from "ag-grid-community";
import { usePostHog } from "posthog-js/react";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import {
  buildPortHeaderName,
  createPhaseGridColumnDefs,
  gridTheme,
  parsePositiveNumber,
} from "./phaseGridConfig";

ModuleRegistry.registerModules([AllCommunityModule]);

/**
 * Checks if a JavaScript object is empty
 * @param {Object} obj - Object to check
 * @returns {boolean} True if object has no own properties
 */
const isEmpty = (obj) => {
  return Object.keys(obj).length === 0 && obj.constructor === Object;
};

const PhaseGrid = ({ setGridApi, rowData, setRowData }) => {
  const theme = useMemo(() => gridTheme, []);
  const posthog = usePostHog();
  const { updatePhase } = usePhases();
  const {
    fetchAndUpdateFleet,
    phases: phaseObj,
    controls,
    advancedControls,
    cityInfo,
    chargerCost,
  } = useProForma();
  const { phases = [] } = phaseObj;
  const { user } = useAuthStore();
  const { phaseColumns, setPhaseColumns } = useColumnState();
  const infrastructureLoanEnabled = Boolean(
    advancedControls?.infrastructure_loan_enabled,
  );
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [siteOptions, setSiteOptions] = useState(
    controls.domiciles?.map((option) => option) || [],
  );
  const [station, setStation] = useState("");
  const { masterAccessIncentiveColumnTitle, fetchMasterAccessData } =
    useAuthStore();

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
  };

  const selectionPortMessageDialog = () => {
    return (
      <Dialog
        open={isDialogOpen}
        onOpenChange={() => setIsDialogOpen()}>
        <DialogContent
          className="sm:max-w-md"
          onOpenAutoFocus={(e) => e.preventDefault()}>
          <DialogHeader>
            {station === "Dual Port" && (
              <>
                {" "}
                <DialogTitle>Dual port:</DialogTitle>
                <DialogDescription>
                  Please enter an even number of ports. Ex: 2, 4, 6, etc
                </DialogDescription>
              </>
            )}
            {station === "Triple Port" && (
              <>
                {" "}
                <DialogTitle>Triple port:</DialogTitle>
                <DialogDescription>
                  Please enter a number of ports that is divisible by three. Ex:
                  3, 6, 9, etc.
                </DialogDescription>
              </>
            )}
          </DialogHeader>
          <DialogFooter className="">
            <DialogClose asChild>
              <Button
                className=""
                type="submit"
                onClick={handleCloseDialog}>
                Close
              </Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  };

  const validateSingleDualTriplePort = (headerName, oldValue, newValue) => {
    if (headerName.includes("Dual Port") && newValue % 2 !== 0) {
      setStation("Dual Port");
      setIsDialogOpen(true);
      return oldValue;
    }

    if (headerName.includes("Triple Port") && newValue % 3 !== 0) {
      setStation("Triple Port");
      setIsDialogOpen(true);
      return oldValue;
    }

    return Number(newValue) < 0 ? Math.abs(Number(newValue)) : Number(newValue);
  };

  /**
   * Initializes the grid and sets up scroll event listener
   * @param {Object} params - Grid API and column API
   */
  const onGridReady = (params) => {
    setGridApi(params.api);
    params.api.addEventListener("bodyScroll", onBodyScroll);
  };

  // Column Definitions: Defines the columns to be displayed.
  const [colDefs, setColDefs] = useState(() =>
    createPhaseGridColumnDefs({
      siteOptions,
      validateSingleDualTriplePort,
      infrastructureLoanEnabled,
    }),
  );

  /**
   * Fetches master access data when user changes and updates column visibility/header names
   */
  useEffect(() => {
    if (user) {
      fetchMasterAccessData();
    }
  }, [user, fetchMasterAccessData]);

  /**
   * Updates column definitions when masterAccessIncentiveColumnTitle changes
   */
  useEffect(() => {
    setColDefs((prevColDefs) =>
      prevColDefs.map((colDef) =>
        colDef.field === "master_access_incentives"
          ? {
              ...colDef,
              hide: !masterAccessIncentiveColumnTitle, // Hide if title is null/empty
              headerName:
                masterAccessIncentiveColumnTitle || "Master Access Incentives",
            }
          : colDef,
      ),
    );
  }, [masterAccessIncentiveColumnTitle]);

  useEffect(() => {
    setSiteOptions(controls.domiciles?.sort().map((option) => option) || []);
  }, [controls]);

  /**
   * Updates column definitions when site options change
   */
  useEffect(() => {
    if (siteOptions.length > 0) {
      setColDefs((prevColDefs) =>
        prevColDefs.map((colDef) =>
          colDef.field === "site"
            ? {
                ...colDef,
                cellEditorParams: {
                  ...colDef.cellEditorParams,
                  values: siteOptions,
                },
              }
            : colDef,
        ),
      );
    }
  }, [siteOptions]);

  useEffect(() => {
    setColDefs((prevColDefs) =>
      prevColDefs.map((colDef) =>
        colDef.field === "loan_amount"
          ? {
              ...colDef,
              hide: !infrastructureLoanEnabled,
            }
          : colDef,
      ),
    );
  }, [infrastructureLoanEnabled]);

  /**
   * Updates port column headers based on city-specific range configurations
   */
  useEffect(() => {
    setColDefs((prevColDefs) =>
      prevColDefs.map((colDef) => {
        if (colDef.field === "port_less_than_10_kw") {
          return {
            ...colDef,
            headerName: buildPortHeaderName(
              cityInfo.port_count_1,
              cityInfo.range_1,
              "<10 kW",
            ),
          };
        } else if (colDef.field === "port_10_20_kw") {
          return {
            ...colDef,
            headerName: buildPortHeaderName(
              cityInfo.port_count_2,
              cityInfo.range_2,
              "10-20 kW",
            ),
          };
        } else if (colDef.field === "port_25_kw") {
          return {
            ...colDef,
            headerName: buildPortHeaderName(
              cityInfo.port_count_3,
              cityInfo.range_3,
              "25 kW",
            ),
          };
        } else if (colDef.field === "port_180_200_kw") {
          return {
            ...colDef,
            headerName: buildPortHeaderName(
              cityInfo.port_count_4,
              cityInfo.range_4,
              "180-200 kW",
            ),
          };
        } else {
          return colDef;
        }
      }),
    );
  }, [cityInfo]);

  /**
   * Handles cell value changes
   * Validates input, updates the backend, and refreshes related data
   * @param {Object} event - Cell value change event
   */
  const handleCellValueChanged = async (event) => {
    const updatedData = event.data;
    const field = event.colDef.field;
    let value = event.newValue;

    if (field === "other_costs_description" && value === null) {
      event.node.setDataValue(field, "");
    } else if (value === null || value === undefined) {
      value = 0;
      event.node.setDataValue(field, value);
    } else if (
      event.colDef.type === "currency" ||
      event.colDef.type === "number"
    ) {
      value = parsePositiveNumber(value);
      event.node.setDataValue(field, value);
    }
    try {
      const result = await updatePhaseField(field, value, updatedData.id);

      await updatePhase(result[0]);
      await fetchAndUpdateFleet("phases");
      posthog.capture("Charger Data Edited");
    } catch (error) {
      console.error("Error updating data:", error);
    }
  };

  const autoSizeStrategy = useMemo(() => {
    return {
      type: "fitCellContents",
      skipHeader: false,
    };
  }, []);

  const onGridPreDestroyed = (event) => {
    setPhaseColumns(event.state);
  };
  const [shadow, setShadow] = useState(true);
  /**
   * Handles horizontal scrolling and updates shadow visibility
   * @param {Object} event - Scroll event
   */
  const onBodyScroll = (event) => {
    const horizontalScrollPosition = event.api.getHorizontalPixelRange();
    // const scrollWidth = event.api.gridPanel.getBodyClientRect().width;
    const size = event.api.getColumnState();
    // const maxScrollLeft = horizontalScrollPosition.right - scrollWidth;
    const totalWidth = size.reduce((total, obj) => {
      if (!obj.pinned && !obj.hide) {
        return total + obj.width;
      }
      return total;
    }, 0);
    if (totalWidth - horizontalScrollPosition.right < 1) {
      setShadow(false);
    } else {
      setShadow(true);
    }
  };

  useEffect(() => {
    if (!Array.isArray(phases)) {
      setRowData([]);
      return;
    }

    const contingency = chargerCost?.contingency
      ? chargerCost.contingency / 100
      : 0;

    const newRowData = phases.map((row) => {
      const loanAmount = infrastructureLoanEnabled ? row.loan_amount || 0 : 0;
      const totalInfraCosts =
        (1 + contingency) * (row.chargerEquipmentCost || 0) +
        (1 + contingency) * (row.installCost || 0) +
        (1 + contingency) * (row.trenching_costs || 0) +
        (1 + contingency) * (row.upgrade_cost_utility || 0) +
        (1 + contingency) * (row.upgrade_cost_customer || 0) +
        (1 + contingency) *
          (row.estimated_public_works_engineering_costs || 0) +
        (1 + contingency) * (row.procurement_management_cost || 0) +
        row.other_costs -
        loanAmount -
        row.capital_planning_funding;

      return {
        ...row,
        chargerInfraTotal: totalInfraCosts,
      };
    });

    setRowData(newRowData);
  }, [chargerCost, phases, infrastructureLoanEnabled, setRowData]);

  const pinnedBottomRowData = useMemo(() => {
    if (!rowData || !Array.isArray(rowData)) {
      return [];
    }

    const totals = rowData.reduce(
      (acc, row) => ({
        port_less_than_10_kw:
          (acc.port_less_than_10_kw || 0) + (row.port_less_than_10_kw || 0),
        port_10_20_kw: (acc.port_10_20_kw || 0) + (row.port_10_20_kw || 0),
        port_25_kw: (acc.port_25_kw || 0) + (row.port_25_kw || 0),
        port_180_200_kw:
          (acc.port_180_200_kw || 0) + (row.port_180_200_kw || 0),
        loan_amount: infrastructureLoanEnabled
          ? (acc.loan_amount || 0) + (row.loan_amount || 0)
          : 0,
        capital_planning_funding:
          (acc.capital_planning_funding || 0) +
          (row.capital_planning_funding || 0),
        trenching_costs:
          (acc.trenching_costs || 0) + (row.trenching_costs || 0),
        upgrade_cost_utility:
          (acc.upgrade_cost_utility || 0) + (row.upgrade_cost_utility || 0),
        upgrade_cost_customer:
          (acc.upgrade_cost_customer || 0) + (row.upgrade_cost_customer || 0),
        procurement_management_cost:
          (acc.procurement_management_cost || 0) +
          (row.procurement_management_cost || 0),
        estimated_public_works_engineering_costs:
          (acc.estimated_public_works_engineering_costs || 0) +
          (row.estimated_public_works_engineering_costs || 0),
        other_costs: (acc.other_costs || 0) + (row.other_costs || 0),
        incentives: (acc.incentives || 0) + (row.incentives || 0),
        master_access_incentives:
          (acc.master_access_incentives || 0) +
          (row.master_access_incentives || 0),
        chargerEquipmentCost:
          (acc.chargerEquipmentCost || 0) + (row.chargerEquipmentCost || 0),
        installCost: (acc.installCost || 0) + (row.installCost || 0),
        chargerInfraTotal:
          (acc.chargerInfraTotal || 0) + (row.chargerInfraTotal || 0),
        totalIncentives:
          masterAccessIncentiveColumnTitle !== null
            ? (acc.master_access_incentives || 0) +
              (row.master_access_incentives || 0) +
              (acc.incentives || 0) +
              (row.incentives || 0)
            : (acc.incentives || 0) + (row.incentives || 0),
      }),
      {},
    );

    return [
      {
        site: "Totals",
        ...totals,
      },
    ];
  }, [rowData, infrastructureLoanEnabled, masterAccessIncentiveColumnTitle]);

  return (
    <div className="w-full h-full">
      <div className="relative">
        {selectionPortMessageDialog()}
        <AgGridReact
          theme={theme}
          initialState={phaseColumns}
          autoSizeStrategy={isEmpty(phaseColumns) ? autoSizeStrategy : {}}
          domLayout="autoHeight"
          stopEditingWhenCellsLoseFocus={true}
          rowData={rowData}
          columnDefs={colDefs}
          rowSelection="single"
          onCellValueChanged={handleCellValueChanged}
          onGridReady={onGridReady}
          suppressColumnVirtualisation={true}
          maintainColumnOrder={true}
          onGridPreDestroyed={onGridPreDestroyed}
          tooltipShowDelay={0}
          pagination={true}
          paginationPageSize={10}
          pinnedBottomRowData={pinnedBottomRowData}
          paginationPageSizeSelector={[10, 20, 30, 50]}
          // paginationAutoPageSize={true}
        />
        {shadow && (
          <div className="h-full absolute top-0 right-0 bottom-0 w-5 bg-gradient-to-r from-transparent to-black/10 pointer-events-none z-20 rounded-lg"></div>
        )}
      </div>
    </div>
  );
};

PhaseGrid.propTypes = {
  gridApi: PropTypes.object,
  setGridApi: PropTypes.func.isRequired,
  rowData: PropTypes.array,
  setRowData: PropTypes.func.isRequired,
};

export default PhaseGrid;
